/* /crm/businesscases/:businesscaseId — één Businesscase op één pagina (ADR 0023).
 *
 *   kop          terug naar de lijst (met de filter), vorige/volgende ([ en ]), acties
 *                (handleiding, grafieken, dupliceren, PDF, archiveren); naam, project,
 *                en de uitkomst: netto investering, terugverdientijd, ROI jaar 1,
 *                opbrengst en kasstroom
 *   sectiebalk   Invoer, Opbrengsten, Grafieken, Notities
 *   rechts       ROI bruto/netto, risico en samenvatting, blijven in beeld
 *
 * Een lege case (geen kosten en geen opbrengsten) zegt dat, met wat je moet invullen.
 * De berekende waarden worden alleen weggeschreven als er iets anders uitkomt. */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Copy, Download, FileText, FolderKanban, MoreHorizontal, Upload } from 'lucide-react';
import { useBusinesscase } from '@/hooks/useBusinesscase';
import { useToast } from '@/hooks/use-toast';
import { calculateBusinesscase } from '@/lib/businesscaseCalculations';
import { BusinesscaseSummary } from '@/components/businesscase/BusinesscaseSummary';
import { CostsInputCard } from '@/components/businesscase/CostsInputCard';
import { SubsidyInputCard } from '@/components/businesscase/SubsidyInputCard';
import { RevenuesTable } from '@/components/businesscase/RevenuesTable';
import { ExtraCostsManager } from '@/components/businesscase/ExtraCostsManager';
import { CashflowChart } from '@/components/businesscase/CashflowChart';
import { ROIGauge } from '@/components/businesscase/ROIGauge';
import { CostsVsRevenuesChart } from '@/components/businesscase/CostsVsRevenuesChart';
import { ZymplerImportModal } from '@/components/businesscase/ZymplerImportModal';
import { PaybackTimeline } from '@/components/businesscase/PaybackTimeline';
import { MonthlyHeatmap } from '@/components/businesscase/MonthlyHeatmap';
import { BusinesscaseGuideModal } from '@/components/businesscase/BusinesscaseGuideModal';
import { RiskIndicator } from '@/components/businesscase/RiskIndicator';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { generateBusinesscasePDF } from '@/lib/businesscasePDF';
import { useChartBulkExport } from '@/hooks/useChartBulkExport';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { euro } from '@/features/projectpagina/ui/opmaak';
import { Sectiebalk, type Sectie } from '@/features/projectpagina/ui/Sectiebalk';
import { Deel, Feit, Navigatie, PaginaMelding, Pil, useRechten } from '@/features/relaties/ui/Onderdelen';
import { Archiveer } from '@/features/relaties/ui/Archiveer';
import { businesscaseLijst, type BcClient } from '@/features/businesscases/data/lijst';
import { archiveerBusinesscase, dupliceerBusinesscase, herstelBusinesscase } from '@/features/businesscases/data/acties';
import { naBusinesscaseWijziging } from '@/features/businesscases/data/sleutels';
import { jaar, procent } from '@/features/businesscases/data/opmaak';

const client = supabase as unknown as BcClient;
const pad = (id: string) => `/crm/businesscases/${id}`;

export default function CRMBusinesscase() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { magBeheren } = useRechten();
  const { businesscaseId } = useParams<{ businesscaseId: string }>();
  const {
    businesscase, project, revenues, extraCosts, loading, fout, updateCosts, updateSubsidies, updateYearRevenue, addYear,
    saveCalculatedValues, updateMetadata, refetch, refetchRevenues,
  } = useBusinesscase(businesscaseId || '');
  const { exportAllCharts } = useChartBulkExport();
  const kopRef = useRef<HTMLElement>(null);

  const [editableName, setEditableName] = useState('');
  const [editableNotes, setEditableNotes] = useState('');
  const [calculations, setCalculations] = useState<any>(null);
  const [zymplerModalOpen, setZymplerModalOpen] = useState(false);
  const [roiMode, setRoiMode] = useState<'bruto' | 'netto'>('bruto');

  useEffect(() => {
    if (businesscase) {
      setEditableName(businesscase.name);
      setEditableNotes(businesscase.notes || '');
      setRoiMode(businesscase.roi_calculation_mode || 'bruto');
    }
  }, [businesscase]);

  useEffect(() => {
    if (!businesscase) return;
    const newCalculations = calculateBusinesscase({
      costs: {
        batteryPurchaseCost: businesscase.battery_purchase_cost,
        installationCost: businesscase.installation_cost,
        softwareSetupCost: businesscase.software_setup_cost,
        annualSubscription: businesscase.annual_subscription,
        otherCosts: businesscase.other_costs,
      },
      subsidies: {
        eiaEnabled: businesscase.eia_enabled,
        flexEEnabled: businesscase.flex_e_enabled,
        flexEValue: businesscase.flex_e_value,
        otherSubsidy: businesscase.other_subsidy,
        taxRate: businesscase.tax_rate,
      },
      revenues,
      extraCosts,
      roiMode,
    });
    setCalculations(newCalculations);
    /* alleen wie mag schrijven, en alleen als er iets anders uitkomt (useBusinesscase) */
    if (magBeheren) saveCalculatedValues(newCalculations);
  }, [businesscase, revenues, extraCosts, roiMode, saveCalculatedValues, magBeheren]);

  if (loading && !businesscase) {
    return <PaginaMelding soort="laden" lijst={businesscaseLijst.adres()} lijstLabel="Businesscases" />;
  }
  if (fout || !businesscase) {
    return (
      <PaginaMelding
        soort={fout?.soort === 'fout' ? 'fout' : 'niet-gevonden'}
        fout={{ message: fout?.bericht ?? 'Deze businesscase bestaat niet (meer).' } as any}
        lijst={businesscaseLijst.adres()}
        lijstLabel="Businesscases"
        opnieuw={fout?.soort === 'fout' ? () => refetch() : undefined}
      />
    );
  }
  if (!calculations) return null;

  const mislukt = (titel: string, e: unknown) => toast({ title: titel, description: (e as Error)?.message ?? String(e), variant: 'destructive' });

  const handleDuplicate = async () => {
    try {
      const { data } = await supabase.auth.getUser();
      const id = await dupliceerBusinesscase(client, businesscase.id, `${businesscase.name} (kopie)`, data.user?.id ?? null);
      naBusinesscaseWijziging(qc);
      toast({ title: 'Businesscase gedupliceerd' });
      navigate(pad(id));
    } catch (e) {
      mislukt('Dupliceren mislukt', e);
    }
  };

  const wissel = async (archiveren: boolean) => {
    try {
      await (archiveren ? archiveerBusinesscase : herstelBusinesscase)(client, businesscase.id);
      naBusinesscaseWijziging(qc);
      toast({ title: archiveren ? 'Businesscase gearchiveerd' : 'Businesscase teruggezet' });
      if (archiveren) navigate(businesscaseLijst.adres());
      else refetch();
    } catch (e) {
      mislukt(archiveren ? 'Archiveren mislukt' : 'Terugzetten mislukt', e);
    }
  };

  const handleNameBlur = () => {
    if (editableName.trim() && editableName !== businesscase.name) {
      /* een fout meldt useBusinesscase zelf */
      updateMetadata({ name: editableName.trim() }).then(() => naBusinesscaseWijziging(qc), () => undefined);
    }
  };

  const handleNotesBlur = () => {
    if (editableNotes !== (businesscase.notes || '')) updateMetadata({ notes: editableNotes }).catch(() => undefined);
  };

  const handleBulkExport = () => {
    const naam = businesscase.name || 'businesscase';
    exportAllCharts([
      { id: 'chart-cashflow', filename: `${naam}_cashflow` },
      { id: 'chart-costs-revenues', filename: `${naam}_kosten_opbrengsten` },
      { id: 'chart-roi', filename: `${naam}_roi` },
      { id: 'chart-payback', filename: `${naam}_terugverdientijd` },
      { id: 'chart-heatmap', filename: `${naam}_heatmap` },
    ], true);
  };

  const handleRoiModeChange = async (mode: 'bruto' | 'netto') => {
    setRoiMode(mode);
    const { error } = await supabase.from('businesscases').update({ roi_calculation_mode: mode }).eq('id', businesscase.id);
    if (error) console.error('Failed to update ROI mode:', error);
  };

  const heeftKosten = calculations.totalInvestment > 0;
  const heeftOpbrengst = calculations.totalRevenues > 0;
  const jaren = revenues.map((r) => r.year).sort((a, b) => a - b);
  const secties: Sectie[] = [
    { id: 'invoer', label: 'Invoer' },
    { id: 'opbrengsten', label: 'Opbrengsten', aantal: revenues.length },
    { id: 'grafieken', label: 'Grafieken' },
    { id: 'notities', label: 'Notities' },
  ];

  return (
    <div className="flex flex-col gap-4" data-bc-staat="klaar">
      <section ref={kopRef} data-bc-kop aria-label="Businesscase" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Navigatie id={businesscase.id} geheugen={businesscaseLijst} terug="Businesscases" pad={pad} enkelvoud="businesscase" />
          <div className="flex items-center gap-2">
            <BusinesscaseGuideModal />
            <Button variant="outline" size="sm" className="h-8" onClick={handleBulkExport} title="Alle grafieken als afbeelding">
              <Download className="mr-1.5 h-4 w-4" />
              <span className="hidden sm:inline">Grafieken</span>
            </Button>
            <Button size="sm" className="h-8" onClick={() => generateBusinesscasePDF({ businesscase, project: project || null, revenues, extraCosts, calculations, roiMode })}>
              <FileText className="mr-1.5 h-4 w-4" />
              PDF
            </Button>
            {magBeheren && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Meer acties">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={handleDuplicate}>
                    <Copy className="mr-2 h-4 w-4" />Dupliceren
                  </DropdownMenuItem>
                  {businesscase.is_archived ? (
                    <DropdownMenuItem onSelect={() => wissel(false)}>
                      <ArchiveRestore className="mr-2 h-4 w-4" />Terugzetten
                    </DropdownMenuItem>
                  ) : (
                    <Archiveer
                      wat="businesscase"
                      naam={businesscase.name}
                      bezwaar={null}
                      terugzetten="onder de tab Gearchiveerd in Businesscases"
                      uitleg="De cijfers blijven bewaard."
                      onArchiveer={() => wissel(true)}
                      trigger={
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <Archive className="mr-2 h-4 w-4" />Archiveren
                        </DropdownMenuItem>
                      }
                    />
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            {magBeheren ? (
              <Input
                aria-label="Naam van de businesscase"
                value={editableName}
                onChange={(e) => setEditableName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                className="-ml-1 h-auto min-w-0 max-w-2xl flex-1 border-transparent px-1 py-0.5 text-2xl font-semibold tracking-tight md:text-2xl shadow-none hover:border-input focus-visible:border-input"
              />
            ) : (
              <h1 className="min-w-0 text-2xl font-semibold tracking-tight">{businesscase.name}</h1>
            )}
            {businesscase.is_archived && <Pil className="border-zinc-200 bg-zinc-100 text-zinc-600">Gearchiveerd</Pil>}
            {!heeftKosten && !heeftOpbrengst && <Pil className="border-amber-200 bg-amber-50 text-amber-800">Leeg</Pil>}
          </div>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {project ? (
              <Link to={`/crm/projects/${project.id}`} data-project-link className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline">
                <FolderKanban className="h-4 w-4" aria-hidden />
                {[project.project_number, project.name].filter(Boolean).join(' · ')}
              </Link>
            ) : (
              <span>Geen project</span>
            )}
            {jaren.length > 0 && <span>{jaren[0]}–{jaren.at(-1)}</span>}
            {businesscase.version > 1 && <span>versie {businesscase.version}</span>}
          </p>
        </div>

        <dl className="m-0 flex flex-wrap items-center gap-x-4 gap-y-2">
          <Feit label="Netto investering">{heeftKosten ? euro(calculations.netInvestment) : '—'}</Feit>
          <Feit label="Terugverdiend">{jaar(heeftKosten ? calculations.paybackPeriod : null)}</Feit>
          <Feit label={`ROI jaar 1 (${roiMode})`}>{procent(calculations.roiYear1)}</Feit>
          <Feit label={`Opbrengst ${revenues.length} jaar`}>{heeftOpbrengst ? euro(calculations.totalRevenues) : '—'}</Feit>
          <Feit label="Kasstroom">{heeftKosten || heeftOpbrengst ? euro(calculations.totalCashflow) : '—'}</Feit>
        </dl>
      </section>

      {!heeftKosten || !heeftOpbrengst ? (
        <div data-bc-leeg className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-amber-300 bg-amber-50/60 px-4 py-2.5 text-sm text-amber-900">
          <span className="min-w-0 flex-1">
            {!heeftKosten && !heeftOpbrengst
              ? 'Deze businesscase is nog leeg. Vul de kosten in, en de opbrengsten per jaar of uit een Zympler-export.'
              : !heeftKosten
                ? 'Er zijn opbrengsten maar nog geen kosten: vul de investering in.'
                : 'Er zijn kosten maar nog geen opbrengsten: vul ze per jaar in of importeer een Zympler-export.'}
          </span>
          {magBeheren && !heeftOpbrengst && (
            <Button size="sm" variant="outline" className="h-8" onClick={() => setZymplerModalOpen(true)}>
              <Upload className="mr-1.5 h-4 w-4" />
              Zympler importeren
            </Button>
          )}
        </div>
      ) : null}

      <Sectiebalk secties={secties} kop={kopRef} titel={businesscase.name} label="Secties van deze businesscase" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-8">
          <Deel id="invoer" titel="Invoer">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <CostsInputCard
                costs={{
                  batteryPurchaseCost: businesscase.battery_purchase_cost,
                  installationCost: businesscase.installation_cost,
                  softwareSetupCost: businesscase.software_setup_cost,
                  annualSubscription: businesscase.annual_subscription,
                  otherCosts: businesscase.other_costs,
                }}
                onUpdate={updateCosts}
              />
              <SubsidyInputCard
                subsidies={{
                  eiaEnabled: businesscase.eia_enabled,
                  flexEEnabled: businesscase.flex_e_enabled,
                  flexEValue: businesscase.flex_e_value,
                  otherSubsidy: businesscase.other_subsidy,
                  taxRate: businesscase.tax_rate,
                }}
                totalInvestment={calculations.totalInvestment}
                eiaValue={calculations.eiaValue}
                onUpdate={updateSubsidies}
              />
            </div>
            <ExtraCostsManager businesscaseId={businesscase.id} extraCosts={extraCosts} onUpdate={refetch} />
          </Deel>

          <Deel id="opbrengsten" titel="Opbrengsten">
            <Card className="p-6">
              <RevenuesTable
                revenues={revenues}
                onUpdateRevenue={updateYearRevenue}
                onAddYear={addYear}
                businesscaseId={businesscase.id}
                onRefetch={refetchRevenues}
                onZymplerClick={() => setZymplerModalOpen(true)}
              />
            </Card>
          </Deel>

          <Deel id="grafieken" titel="Grafieken">
            <div className="flex flex-col gap-6">
              <div id="chart-cashflow"><CashflowChart data={calculations.yearlyData} businesscaseName={editableName} /></div>
              <div id="chart-costs-revenues"><CostsVsRevenuesChart data={calculations.yearlyData} businesscaseName={editableName} /></div>
              <div id="chart-roi">
                <ROIGauge roiYear1={calculations.roiYear1} roiBest={calculations.roiBestCase} roiWorst={calculations.roiWorstCase} businesscaseName={editableName} />
              </div>
              <div id="chart-payback"><PaybackTimeline yearlyData={calculations.yearlyData} paybackPeriod={calculations.paybackPeriod} /></div>
              <div id="chart-heatmap"><MonthlyHeatmap yearlyData={calculations.yearlyData} /></div>
            </div>
          </Deel>

          <Deel id="notities" titel="Notities">
            <Label htmlFor="bc-notes" className="sr-only">Notities</Label>
            <Textarea
              id="bc-notes"
              value={editableNotes}
              onChange={(e) => setEditableNotes(e.target.value)}
              onBlur={handleNotesBlur}
              readOnly={!magBeheren}
              placeholder="Context, aannames, wat er met de klant is afgesproken…"
              rows={4}
            />
          </Deel>
        </div>

        <div className="lg:col-span-4">
          <div className="flex flex-col gap-4 lg:sticky lg:top-14">
            <Card className="border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold">Return on Investment (ROI)</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {roiMode === 'bruto' ? 'Bruto: omzet gedeeld door investering' : 'Netto: omzet minus kosten (OPEX) gedeeld door investering'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="roi-mode" className={cn('cursor-pointer text-sm transition-colors', roiMode === 'bruto' ? 'font-semibold text-primary' : 'text-muted-foreground')}>Bruto</Label>
                  <Switch id="roi-mode" checked={roiMode === 'netto'} disabled={!magBeheren} onCheckedChange={(checked) => handleRoiModeChange(checked ? 'netto' : 'bruto')} />
                  <Label htmlFor="roi-mode" className={cn('cursor-pointer text-sm transition-colors', roiMode === 'netto' ? 'font-semibold text-primary' : 'text-muted-foreground')}>Netto</Label>
                </div>
              </div>
            </Card>
            <RiskIndicator calculations={calculations} />
            <BusinesscaseSummary
              calculations={calculations}
              flexEEnabled={businesscase.flex_e_enabled}
              flexEValue={businesscase.flex_e_value}
              otherSubsidy={businesscase.other_subsidy}
              businesscaseName={editableName}
              roiMode={roiMode}
            />
          </div>
        </div>
      </div>

      <ZymplerImportModal
        open={zymplerModalOpen}
        onOpenChange={setZymplerModalOpen}
        businesscaseId={businesscase.id}
        existingRevenues={revenues}
        onImportComplete={async () => {
          await refetchRevenues();
        }}
      />
    </div>
  );
}
